import * as styles from "./fields.css";

import { clsx } from "clsx";
import { useAtomValue } from "jotai";
import { CircleMinus } from "lucide-react";
import React from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  Field,
  FieldContent,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import { useArrayField } from "@frontend/hooks/form";
import type { ArrayField, Field as FieldType } from "@frontend/lib/form";
import { Trans } from "@lingui/react/macro";

import { isEditModeAtom } from "../../stores";

function RemoveButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      size="sm"
      variant="ghost"
      className={styles.removeButton}
      // The button sits inside a <label>, whose default behaviour would
      // forward the click to the row's control
      onClick={(event) => {
        event.preventDefault();
        onClick();
      }}
    >
      <CircleMinus size={14} />
    </Button>
  );
}

export function FieldList<TField extends FieldType, TValue>({
  field,
  fieldAlign = "center",
  renderLeft,
  renderRight,
  renderEmpty,
  onRemove,
}: {
  field: ArrayField<TField, TValue>;
  fieldAlign?: "center" | "start";
  renderLeft: (item: TField) => React.ReactNode;
  renderRight: (item: TField) => React.ReactNode;
  renderEmpty?: () => React.ReactNode;
  onRemove: (index: number) => void;
}) {
  const editMode = useAtomValue(isEditModeAtom);

  const { items } = useArrayField(field);

  return (
    <>
      {items.length === 0 && (
        <div className={styles.empty}>
          {renderEmpty ? renderEmpty() : <Trans>No items</Trans>}
        </div>
      )}

      {items.map((item, index) => (
        <Field
          key={item.path}
          className={clsx(
            styles.row,
            !editMode && styles.rowHoverable,
            fieldAlign === "start" && styles.rowStart,
          )}
        >
          <FieldLabel>
            {editMode && <RemoveButton onClick={() => onRemove(index)} />}
            {renderLeft(item)}
          </FieldLabel>

          <FieldContent>{renderRight(item)}</FieldContent>
        </Field>
      ))}
    </>
  );
}
